import axios from 'axios';
import dayjs from 'dayjs';
import * as sessionStore from './sessionStore';
import { Category, Supercategory, Product, ProductCardOptions, ProductPackage, User, Order, PromoCode, District, AdminUser, AdminUserCreate, AdminUserUpdate, AdminUserStats, PaginatedResponse, FAQ, FAQCreate, FAQUpdate, DeliverySlot, DeliverySlotCreate, DeliveryDateOverride, DeliveryDateOverrideUpsert, CompanyInfo, CompanyInfoCreate, AppInfo, AppInfoCreate, AppInfoUpdate, BusinessConfig, BusinessConfigResponse, Campaign, CampaignCreate, CampaignUpdate, CampaignStats, CampaignOverview, Marker, MarkerCreate, MarkerUpdate, WarehouseOverview, StockInfo, StockMovement, PaginatedStockMovements, StockRestock, StockAdjustment, StockWriteOff, StockAlert, DeliveryResponse, DeliveryCreate, ReceiveDeliveryInput, ShelfResponse, ShelfAssignmentResponse, Promotion, PromotionInput, PromotionPreview, UserDetail, CustomerNote, CustomerTag, ManualOrderPayload, ManualOrderPreview, ManualOrderCreated, OrderEditPayload, OrderEditPreview, OrderEditResult, PaymentListParams, PaymentListResponse, PaymentRow } from '../types';
import type {
  BrandingTexts,
  BrandTextsResponse,
  GenerateBrandingPayload,
} from '../types/branding';
import type {
  LegalDocument,
  LegalDocumentCreate,
  LegalDocumentSummary,
  LegalDocumentUpdate,
} from '../types/legal';
import type { Recipe, RecipeFields, RecipeUpdate } from '../types/recipe';
import {
  ProductStatsQuery,
  ProductStatsOverview,
  ProductSalesResponse,
  ProductTrendResponse,
  CategoryBreakdownResponse,
  EngagementFunnelResponse,
  TopProductsResponse,
  PeriodComparisonResponse,
  CategoryProductsBreakdownResponse,
  CustomerStats,
  OrdersExtendedStats,
  DistrictStats,
  WeekdayOrderStats,
  UserStatsQuery,
  UserStatsOverview,
  UserSegmentationResponse,
  RetentionResponse,
  UserGrowthResponse,
  TopCustomersResponse,
  UserExtendedStats,
  NewVsReturningRevenue,
  ActivityHeatmapResponse,
  PaymentMethodsResponse,
  BroadcastStatsResponse,
  ActiveUsersTrendResponse,
  VisitorsTrendResponse,
  WebFunnelResponse,
  WebFunnelTrendPoint,
  EngagementStatsResponse,
} from '../types/statistics';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api/v1';

// Every product endpoint lives under /api/v1, but the OAuth server mounts its
// router at the API root (`/oauth/...`). Absolute on purpose: axios skips
// baseURL only for a url with a scheme, so a bare '/oauth/...' would still be
// glued onto '/api/v1' and 404.
export const API_ROOT_URL = new URL(
  API_BASE_URL.replace(/\/api\/v1\/?$/, '') || '/',
  window.location.origin
).toString().replace(/\/+$/, '');

// Create axios instance for API calls
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true  // Also send cookies when available
});

// Dual authentication: Authorization header (primary) + cookies (same-domain fallback)
api.interceptors.request.use((config) => {
  const token = sessionStore.getActiveAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Silent refresh. Single-flight: concurrent 401s share one refresh attempt.
let refreshInFlight: Promise<string | null> | null = null;

async function refreshSession(memberId: number): Promise<string | null> {
  const s = sessionStore.getSession(memberId);
  if (!s?.refreshToken) return null;
  try {
    // Bare axios on purpose — the `api` instance would recurse into the 401 handler.
    const resp = await axios.post(
      `${API_BASE_URL}/admin/auth/refresh`,
      { refresh_token: s.refreshToken },
      { headers: { 'Content-Type': 'application/json' }, withCredentials: true }
    );
    sessionStore.updateTokens(memberId, resp.data.access_token, resp.data.refresh_token);
    return resp.data.access_token;
  } catch {
    return null;
  }
}

export async function refreshActiveSession(): Promise<string | null> {
  const id = sessionStore.getActiveMemberId();
  if (id === null) return null;
  if (!refreshInFlight) {
    refreshInFlight = refreshSession(id).finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

// Drop only the FAILED account; other saved accounts stay logged in and are
// offered again on the login page. Takes the member id captured at 401 time:
// removeSession(id) is idempotent, so when N concurrent 401s all land here
// they drop the same dead account once instead of cascade-deleting the
// accounts that removeActiveSession() would keep promoting.
export function dropActiveSessionAndRedirect(failedMemberId: number | null): void {
  if (failedMemberId !== null) {
    sessionStore.removeSession(failedMemberId);
  }
  window.dispatchEvent(new CustomEvent('admin_logout'));
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
}

// A 401 from these endpoints is a real auth answer (bad password / dead
// refresh token), not an expired session — never run the refresh-and-retry
// dance for them, and never nuke the active session because of them.
const AUTH_PATHS = ['/admin/auth/login', '/admin/auth/refresh', '/admin/auth/logout'];

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = (error.config ?? {}) as any;
    const isAuthCall = AUTH_PATHS.some((p) => (original.url ?? '').includes(p));

    if (error.response?.status === 401 && !isAuthCall) {
      // Captured BEFORE the refresh await: by the time a failed refresh
      // resolves, a concurrent 401 may already have dropped this account and
      // promoted another — we must not drop the promoted one too.
      const failedId = sessionStore.getActiveMemberId();
      if (!original._retry) {
        original._retry = true;
        const token = await refreshActiveSession();
        if (token) {
          original.headers = original.headers ?? {};
          original.headers.Authorization = `Bearer ${token}`;
          return api(original);
        }
      }
      dropActiveSessionAndRedirect(failedId);
      return Promise.reject(error);
    }

    // Handle network errors
    if (!error.response) {
      error.message = 'Помилка мережі. Перевірте підключення до інтернету.';
    }

    // Handle server errors
    if (error.response?.status >= 500) {
      error.message = 'Помилка сервера. Спробуйте пізніше.';
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: async (
    username: string,
    password: string,
    remember?: boolean,
    org?: string
  ): Promise<AdminUser> => {
    const response = await api.post('/admin/auth/login', {
      username,
      password,
      ...(org ? { org } : {}),
    });
    const d = response.data;
    const admin: AdminUser = {
      id: d.id,
      username: d.username,
      email: d.email,
      is_active: d.is_active,
      is_superadmin: d.is_superadmin ?? false,
      role: d.role ?? 'admin',
      created_at: d.created_at,
      last_login: d.last_login,
    };
    const membership = (d.memberships ?? []).find(
      (m: { org_slug: string; org_name?: string }) => m.org_slug === d.org
    );
    sessionStore.upsertSession({
      user: admin,
      org: d.org ?? null,
      orgName: membership?.org_name ?? null,
      accessToken: d.access_token,
      // «Запам'ятати мене» → keep the refresh token so the account silently
      // renews (~7 days idle); otherwise it dies with its access token (the
      // old session-only behavior).
      refreshToken: remember ? d.refresh_token ?? null : null,
      addedAt: new Date().toISOString(),
    });
    return admin;
  },

  // Logs out ONLY the active account. Returns the member id of the account
  // that became active instead, or null when none remain.
  logout: async (): Promise<number | null> => {
    try {
      // Clears the httpOnly cookies; harmless for the other saved accounts
      // since the Authorization header is the primary credential now.
      await api.post('/admin/auth/logout');
    } catch (error) {
      console.warn('Logout request failed:', error);
    }
    const next = sessionStore.removeActiveSession();
    window.dispatchEvent(new CustomEvent('admin_logout'));
    return next;
  },

  refresh: async (): Promise<{ access_token: string }> => {
    const token = await refreshActiveSession();
    if (!token) throw new Error('Не вдалося оновити сесію');
    return { access_token: token };
  },

  // Try to re-enter a saved account from the login page: use its stored
  // access token, fall back to its refresh token. On success it becomes the
  // active account. The session entry is kept even on failure so the account
  // chip stays on the login page (the user just re-enters the password).
  resumeSession: async (memberId: number): Promise<boolean> => {
    const s = sessionStore.getSession(memberId);
    if (!s) return false;
    try {
      await axios.get(`${API_BASE_URL}/admin/auth/me`, {
        headers: { Authorization: `Bearer ${s.accessToken}` },
      });
      sessionStore.setActive(memberId);
      return true;
    } catch {
      // expired access token — try the refresh token
    }
    const token = await refreshSession(memberId);
    if (token) {
      sessionStore.setActive(memberId);
      return true;
    }
    return false;
  },

  getCurrentUser: async (): Promise<AdminUser> => {
    const response = await api.get('/admin/auth/me');
    return response.data;
  },

  verifyToken: async (): Promise<boolean> => {
    try {
      await api.get('/admin/auth/me');
      return true;
    } catch {
      return false;
    }
  }
};

// Categories API
export const categoriesAPI = {
  getAll: async (): Promise<Category[]> => {
    const response = await api.get('/admin/categories');
    return response.data;
  },

  create: async (category: Omit<Category, 'id'>): Promise<Category> => {
    const response = await api.post('/admin/categories', category);
    return response.data;
  },

  update: async (id: string, category: Partial<Category>): Promise<Category> => {
    const response = await api.put(`/admin/categories/${id}`, category);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/admin/categories/${id}`);
  },

  getSupercategories: async (): Promise<Record<string, any[]>> => {
    const response = await api.get('/categories/supercategories');
    return response.data;
  }
};

// Supercategories API (first-class entity)
export const supercategoriesAPI = {
  getAll: async (): Promise<Supercategory[]> => {
    const response = await api.get('/admin/supercategories');
    return response.data;
  },

  create: async (payload: {
    id: string;
    name: string;
    slug?: string;
    icon?: string;
    description?: string;
    order?: number;
    is_active?: boolean;
  }): Promise<Supercategory> => {
    const response = await api.post('/admin/supercategories', payload);
    return response.data;
  },

  update: async (
    id: string,
    patch: Partial<{
      name: string;
      slug: string;
      icon: string;
      description: string;
      order: number;
      is_active: boolean;
    }>
  ): Promise<Supercategory> => {
    const response = await api.put(`/admin/supercategories/${id}`, patch);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/admin/supercategories/${id}`);
  },
};

// Products API
export const productsAPI = {
  getAll: async (
    page = 1,
    size = 20,
    search?: string,
    categoryId?: string,
    isActive?: boolean,
    isFixedPackage?: boolean,
    isFeatured?: boolean,
    archived?: boolean
  ): Promise<PaginatedResponse<Product>> => {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
    });

    if (search) params.append('search', search);
    if (categoryId) params.append('category_id', categoryId);
    if (isActive !== undefined) params.append('is_active', isActive.toString());
    if (isFixedPackage !== undefined) params.append('is_fixed_package', isFixedPackage.toString());
    if (isFeatured !== undefined) params.append('is_featured', isFeatured.toString());
    if (archived) params.append('archived', 'true');

    const response = await api.get(`/admin/products?${params.toString()}`);
    return response.data;
  },

  getById: async (id: string): Promise<Product> => {
    const response = await api.get(`/admin/products/${id}`);
    return response.data;
  },

  // Selector data for the shared product-card block (countries directory,
  // brands/packaging types already used in this catalog + presets)
  getCardOptions: async (): Promise<ProductCardOptions> => {
    const response = await api.get('/admin/products/card-options');
    return response.data;
  },

  create: async (product: Omit<Product, 'id' | 'category'>): Promise<Product> => {
    const response = await api.post('/admin/products', product);
    return response.data;
  },

  update: async (id: string, product: Partial<Product>): Promise<Product> => {
    const response = await api.put(`/admin/products/${id}`, product);
    return response.data;
  },

  // «Видалення» товару — це архівування: order_items посилається на products,
  // тож фізично стерти товар, який колись замовляли, база не дасть.
  delete: async (id: string): Promise<void> => {
    await api.delete(`/admin/products/${id}`);
  },

  restore: async (id: string): Promise<Product> => {
    const response = await api.post(`/admin/products/${id}/restore`);
    return response.data;
  },

  // purpose='favicon' просить бекенд нормалізувати файл у квадратний PNG і відхиляє SVG.
  uploadImage: async (file: File, folder?: string, purpose?: string): Promise<{ url: string }> => {
    const formData = new FormData();
    formData.append('image', file);
    if (folder) formData.append('folder', folder);
    if (purpose) formData.append('purpose', purpose);
    const response = await api.post('/admin/upload/image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  getStats: async (): Promise<{
    total_products: number;
    total_categories: number;
    featured_products: number;
    active_products: number;
  }> => {
    const response = await api.get('/admin/products/stats');
    return response.data;
  },

  bulkUpdateOrder: async (updates: Array<{ id: string; catalog_order: number }>): Promise<{
    message: string;
    updated_count: number;
  }> => {
    const response = await api.put('/admin/products/bulk-update-order', { updates });
    return response.data;
  }
};

// Users API
export const usersAPI = {
  getAll: async (
    page = 1,
    size = 20,
    search?: string,
    status?: string,
    segment?: string,
    sortBy?: string,
    sortDir?: string
  ): Promise<PaginatedResponse<User>> => {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
    });

    if (search) params.append('search', search);
    if (status) params.append('status', status);
    if (segment) params.append('segment', segment);
    if (sortBy) params.append('sort_by', sortBy);
    if (sortDir) params.append('sort_dir', sortDir);

    const response = await api.get(`/admin/users?${params.toString()}`);
    return response.data;
  },

  getById: async (id: number): Promise<User> => {
    const response = await api.get(`/admin/users/${id}`);
    return response.data;
  },

  getCard: async (id: number): Promise<UserDetail> => {
    const response = await api.get(`/admin/users/${id}/card`);
    return response.data;
  },

  addNote: async (id: number, content: string): Promise<CustomerNote> => {
    const response = await api.post(`/admin/users/${id}/notes`, { content });
    return response.data;
  },

  deleteNote: async (id: number, noteId: number): Promise<void> => {
    await api.delete(`/admin/users/${id}/notes/${noteId}`);
  },

  addTag: async (id: number, tag: string): Promise<CustomerTag> => {
    const response = await api.post(`/admin/users/${id}/tags`, { tag });
    return response.data;
  },

  deleteTag: async (id: number, tagId: number): Promise<void> => {
    await api.delete(`/admin/users/${id}/tags/${tagId}`);
  },

  aiSummary: async (id: number): Promise<string> => {
    const response = await api.post(`/admin/users/${id}/ai-summary`);
    return response.data.summary;
  },

  sendMessage: async (id: number, message: string): Promise<void> => {
    await api.post(`/admin/users/${id}/message`, { user_id: id, message });
  },

  sendCartReminder: async (
    id: number,
  ): Promise<{ status: string; sent_at?: string | null }> => {
    const response = await api.post(`/admin/users/${id}/cart-reminder`);
    return response.data;
  },

  update: async (id: number, user: Partial<User>): Promise<User> => {
    const response = await api.put(`/admin/users/${id}`, user);
    return response.data;
  },

  getStats: async (): Promise<{
    total: number;
    active: number;
    gold_clients: number;
    blocked: number;
  }> => {
    const response = await api.get('/admin/users/stats');
    return response.data;
  }
};

// Admin Users API (for managing admin accounts)
export const adminUsersAPI = {
  getAll: async (
    page = 1,
    size = 20,
    search?: string,
    status?: string
  ): Promise<PaginatedResponse<AdminUser>> => {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
    });

    if (search) params.append('search', search);
    if (status) params.append('status', status);

    const response = await api.get(`/admin/admin-users?${params.toString()}`);
    return response.data;
  },

  getById: async (id: number): Promise<AdminUser> => {
    const response = await api.get(`/admin/admin-users/${id}`);
    return response.data;
  },

  getStats: async (): Promise<AdminUserStats> => {
    const response = await api.get('/admin/admin-users/stats');
    return response.data;
  },

  create: async (data: AdminUserCreate): Promise<AdminUser> => {
    const response = await api.post('/admin/admin-users', data);
    return response.data;
  },

  update: async (id: number, data: AdminUserUpdate): Promise<AdminUser> => {
    const response = await api.put(`/admin/admin-users/${id}`, data);
    return response.data;
  },

  changePassword: async (id: number, newPassword: string): Promise<void> => {
    await api.put(`/admin/admin-users/${id}/password`, { new_password: newPassword });
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/admin/admin-users/${id}`);
  }
};

// Team API (central-identity org members)
export interface TeamMember { id: number; username: string | null; email: string; role: string; is_active: boolean; }
export const teamAPI = {
  list: async (): Promise<TeamMember[]> => (await api.get('/admin/team')).data,
  invite: async (data: { email: string; username: string; role: string; password: string }) =>
    (await api.post('/admin/team', data)).data,
  update: async (id: number, data: { role?: string; is_active?: boolean }) =>
    (await api.patch(`/admin/team/${id}`, data)).data,
  remove: async (id: number) => { await api.delete(`/admin/team/${id}`); },
};

// Orders API
export type CustomerType = 'all' | 'tg' | 'guest';

export const ordersAPI = {
  getAll: async (
    page = 1,
    size = 20,
    status?: string,
    startDate?: string,
    endDate?: string,
    search?: string,
    deliveryMethod?: string,
    customerType?: CustomerType,
    isTest?: boolean,
    npState?: 'without_ttn' | 'with_alerts'
  ): Promise<PaginatedResponse<Order>> => {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
    });

    if (status) params.append('status', status);
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (search) params.append('search', search);
    if (deliveryMethod) params.append('delivery_method', deliveryMethod);
    if (customerType && customerType !== 'all') params.append('customer_type', customerType);
    if (isTest !== undefined) params.append('is_test', String(isTest));
    if (npState) params.append('np_state', npState);

    const response = await api.get(`/admin/orders?${params.toString()}`);
    return response.data;
  },

  getById: async (id: number): Promise<Order> => {
    const response = await api.get(`/admin/orders/${id}`);
    return response.data;
  },

  getForVerification: async (id: number): Promise<Order> => {
    const response = await api.get(`/admin/orders/${id}/verify`);
    return response.data;
  },

  submitVerification: async (id: number, verificationData: any): Promise<Order> => {
    const response = await api.post(`/admin/orders/${id}/verify`, verificationData);
    return response.data;
  },

  confirmOrder: async (id: number, sendNotification: boolean = true): Promise<Order> => {
    const response = await api.post(`/admin/orders/${id}/confirm`, {
      send_notification: sendNotification
    });
    return response.data;
  },

  updateItemWeight: async (orderId: number, itemId: number, weight: number): Promise<Order> => {
    const response = await api.put(`/admin/orders/${orderId}/items/${itemId}/weight`, { weight });
    return response.data;
  },

  updateStatus: async (id: number, status: Order['status']): Promise<Order> => {
    const response = await api.put(`/admin/orders/${id}/status`, { status });
    return response.data;
  },

  // Тестове замовлення лишається у списку зі своїм статусом, але випадає зі
  // всієї статистики (cancel rate, виручка, топ-товари, сегменти клієнтів).
  setTestFlag: async (id: number, isTest: boolean): Promise<Order> => {
    const response = await api.put(`/admin/orders/${id}/test-flag`, { is_test: isTest });
    return response.data;
  },

  // New verification endpoints
  addVerificationItem: async (orderId: number, data: {
    product_id: string;
    package_id: string;
    quantity: number;
    actual_weight?: number;
    actual_quantity?: number;
    actual_price_per_unit?: number;
  }): Promise<Order> => {
    const response = await api.post(`/admin/orders/${orderId}/verify/items/add`, data);
    return response.data;
  },

  removeVerificationItem: async (itemId: number): Promise<{ message: string; order: Order }> => {
    const response = await api.delete(`/admin/verify/items/${itemId}`);
    return response.data;
  },

  getVerificationHistory: async (orderId: number): Promise<any[]> => {
    const response = await api.get(`/admin/orders/${orderId}/verification-history`);
    return response.data;
  },

  getStats: async (startDate?: string, endDate?: string): Promise<{
    total_orders: number;
    total_revenue: number;
    avg_order_value: number;
    orders_by_status: Record<string, number>;
    avg_processing_time_minutes: number | null;
  }> => {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);

    const response = await api.get(`/admin/orders/stats?${params.toString()}`);
    return response.data;
  },

  getWeeklyStats: async (): Promise<Array<{
    name: string;
    date: string;
    orders: number;
    revenue: number;
  }>> => {
    const response = await api.get('/admin/orders/weekly-stats');
    return response.data;
  },

  exportReport: async (startDate: string, endDate: string): Promise<Blob> => {
    const response = await api.get(`/admin/orders/export`, {
      params: { start_date: startDate, end_date: endDate },
      responseType: 'blob',
    });
    return response.data;
  },

  verifyWeights: async (
    orderId: number,
    items: any[],
    managerName: string,
    sendNotification: boolean = true,
    managerComment?: string
  ): Promise<Order> => {
    console.log('verifyWeights called with:', { orderId, items, managerName, sendNotification, managerComment });

    const payload = {
      items: items.map(item => ({
        id: item.id,
        actual_weight: item.actual_weight,
        actual_quantity: item.actual_quantity,
        actual_total_price: item.actual_total_price,
        weight_variance_percent: item.weight_variance_percent,
        price_variance_percent: item.price_variance_percent
      })),
      manager_name: managerName,
      send_notification: sendNotification,
      manager_comment: managerComment || ''
    };

    console.log('Sending payload:', JSON.stringify(payload, null, 2));

    const response = await api.post(`/admin/orders/${orderId}/verify-weights`, payload);
    return response.data;
  },

  changePrice: async (orderId: number, data: {
    new_total_amount: number;
    reason: string;
    justification: string;
    manager_approval_required?: boolean;
  }): Promise<Order> => {
    const response = await api.put(`/admin/orders/${orderId}/change-price`, data);
    return response.data;
  },

  // Receipt Management
  generateReceipt: async (orderId: number): Promise<{
    message: string;
    receipt_url: string | null;
    s3_key: string | null;
    generated_at: string;
  }> => {
    const response = await api.post(`/admin/orders/${orderId}/generate-receipt`);
    return response.data;
  },

  getReceiptUrl: async (orderId: number): Promise<{
    data: {
      receipt_url: string | null;
      s3_key: string | null;
      generated_at: string | null;
      expires_in?: number;
      message?: string;
    }
  }> => {
    const response = await api.get(`/admin/orders/${orderId}/receipt`);
    return response;
  },

  sendReceiptToCustomer: async (orderId: number): Promise<{
    message: string;
    user_id: number;
    order_id: number;
  }> => {
    const response = await api.post(`/admin/orders/${orderId}/send-receipt`);
    return response.data;
  },

  getPriceHistory: async (orderId: number): Promise<{
    order_id: number;
    current_total: number;
    price_history: any[];
    total_changes: number;
  }> => {
    const response = await api.get(`/admin/orders/${orderId}/price-history`);
    return response.data;
  },

  resendPaymentLink: async (id: number): Promise<Order> => {
    const response = await api.post(`/admin/orders/${id}/resend-payment-link`, {});
    return response.data;
  },

  /** Позначити оплату готівкою/IBAN отриманою вручну — для картки цей момент дає Monobank сам. */
  markPaymentReceived: async (id: number): Promise<Order> => {
    const response = await api.post(`/admin/orders/${id}/payment-received`, {});
    return response.data;
  },
};

// Monobank invoices list — read-only, backed by the local `payments` table
// (no live call to the bank). See app/api/endpoints/admin_payments.py.
export const paymentsAPI = {
  buildParams: (params: PaymentListParams = {}): URLSearchParams => {
    const searchParams = new URLSearchParams();
    searchParams.append('page', String(params.page ?? 1));
    searchParams.append('size', String(params.size ?? 50));
    if (params.status) searchParams.append('status', params.status);
    if (params.kind) searchParams.append('kind', params.kind);
    // start_date лишається голим YYYY-MM-DD — бекенд парсить його як північ,
    // тобто вже початок доби, як і треба.
    if (params.start_date) searchParams.append('start_date', params.start_date);
    // end_date навпаки розширюємо до кінця доби: бекенд фільтрує
    // `created_at <= end_date`, і гола дата парситься як 00:00 — без цього
    // вся обрана доба мовчки випадає з вибірки.
    if (params.end_date) {
      const endOfDay = dayjs(params.end_date).endOf('day').format('YYYY-MM-DDTHH:mm:ss');
      searchParams.append('end_date', endOfDay);
    }
    if (params.search) searchParams.append('search', params.search);
    return searchParams;
  },

  list: async (params: PaymentListParams = {}): Promise<PaymentListResponse> => {
    const searchParams = paymentsAPI.buildParams(params);
    const response = await api.get(`/admin/payments?${searchParams.toString()}`);
    return response.data;
  },

  // Один інвойс — для прев'ю сутності (EntityPreview). Той самий рядок,
  // що й у списку (backend будує обидва з однієї функції _to_row), тож
  // фронту не потрібен окремий тип.
  getById: async (id: number): Promise<PaymentRow> => {
    const response = await api.get(`/admin/payments/${id}`);
    return response.data;
  },
};

// Districts API
export const districtsAPI = {
  getAll: async (): Promise<District[]> => {
    const response = await api.get('/admin/districts');
    return response.data;
  },

  create: async (district: Omit<District, 'id'>): Promise<District> => {
    const response = await api.post('/admin/districts', district);
    return response.data;
  },

  update: async (id: number, district: Partial<District>): Promise<District> => {
    const response = await api.put(`/admin/districts/${id}`, district);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/admin/districts/${id}`);
  }
};

// Markers API
export const markersAPI = {
  getAll: async (): Promise<Marker[]> => {
    const response = await api.get('/admin/markers');
    return response.data;
  },

  getById: async (id: number): Promise<Marker> => {
    const response = await api.get(`/admin/markers/${id}`);
    return response.data;
  },

  create: async (marker: MarkerCreate): Promise<Marker> => {
    const response = await api.post('/admin/markers', marker);
    return response.data;
  },

  update: async (id: number, marker: MarkerUpdate): Promise<Marker> => {
    const response = await api.put(`/admin/markers/${id}`, marker);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/admin/markers/${id}`);
  }
};

// Promo codes API
export const promoCodesAPI = {
  getAll: async (): Promise<PromoCode[]> => {
    const response = await api.get('/admin/promo-codes');
    return response.data;
  },

  create: async (promoCode: Omit<PromoCode, 'id' | 'usage_count'>): Promise<PromoCode> => {
    const response = await api.post('/admin/promo-codes', promoCode);
    return response.data;
  },

  update: async (id: number, promoCode: Partial<PromoCode>): Promise<PromoCode> => {
    const response = await api.put(`/admin/promo-codes/${id}`, promoCode);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/admin/promo-codes/${id}`);
  }
};

// Promotions API (auto-discounts on catalog)
export const promotionsAPI = {
  getAll: async (): Promise<Promotion[]> => {
    const response = await api.get('/admin/promotions/');
    return response.data;
  },

  getById: async (id: number): Promise<Promotion> => {
    const response = await api.get(`/admin/promotions/${id}`);
    return response.data;
  },

  create: async (promotion: PromotionInput): Promise<Promotion> => {
    const response = await api.post('/admin/promotions/', promotion);
    return response.data;
  },

  update: async (id: number, promotion: PromotionInput): Promise<Promotion> => {
    const response = await api.put(`/admin/promotions/${id}`, promotion);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/admin/promotions/${id}`);
  },

  preview: async (id: number): Promise<PromotionPreview> => {
    const response = await api.get(`/admin/promotions/${id}/preview`);
    return response.data;
  }
};

// Google Merchant Center feed
export interface FeedIssue {
  item_id: string | null;
  severity: 'error' | 'warning';
  code: string;
  message: string;
}

export interface FeedValidation {
  checked_at: string;
  items_total: number;
  errors: number;
  warnings: number;
  urls_checked: boolean;
  issues: FeedIssue[];
}

export interface TaxonomyOption {
  id: string;
  label: string;
}

export interface GmcAttributeSpec {
  name: string;
  level: 'required' | 'recommended';
  input: 'select' | 'text';
  label: string;
  values: string[] | null;
  max_length: number | null;
  help: string | null;
}

export interface CategoryAttributeSpec {
  category_id: string;
  known: boolean;
  branch: TaxonomyOption | null;
  attributes: GmcAttributeSpec[];
}

export const feedAPI = {
  validate: async (checkUrls = false): Promise<FeedValidation> => {
    const response = await api.get('/admin/feed/validate', {
      params: { check_urls: checkUrls },
      // Checking every product page and image is one request per URL.
      timeout: checkUrls ? 180000 : 60000,
    });
    return response.data;
  },
  // Search the vendored Google taxonomy; `ids` hydrates labels for values
  // that are already stored in a form.
  searchTaxonomy: async (q = '', ids: string[] = []): Promise<TaxonomyOption[]> => {
    const response = await api.get('/admin/feed/taxonomy', {
      params: { q, ids: ids.join(',') },
    });
    return response.data;
  },
  // Attribute spec for a Google category id: branch-specific + common.
  categoryAttributes: async (categoryId: string): Promise<CategoryAttributeSpec> => {
    const response = await api.get(`/admin/feed/taxonomy/${categoryId}/attributes`);
    return response.data;
  },
};

// Product Packages API
export const packagesAPI = {
  getByProduct: async (productId: string): Promise<ProductPackage[]> => {
    const response = await api.get(`/admin/products/${productId}/packages`);
    return response.data;
  },

  create: async (productId: string, packageData: Omit<ProductPackage, 'id' | 'product_id' | 'created_at' | 'updated_at'>): Promise<ProductPackage> => {
    const response = await api.post(`/admin/products/${productId}/packages`, packageData);
    return response.data;
  },

  update: async (productId: string, packageId: number, packageData: Partial<ProductPackage>): Promise<ProductPackage> => {
    const response = await api.put(`/admin/products/${productId}/packages/${packageId}`, packageData);
    return response.data;
  },

  delete: async (productId: string, packageId: number): Promise<void> => {
    await api.delete(`/admin/products/${productId}/packages/${packageId}`);
  },

  uploadImage: async (productId: string, packageId: number, file: File): Promise<{ url: string }> => {
    const formData = new FormData();
    formData.append('image', file);

    const response = await api.post(`/admin/products/${productId}/packages/${packageId}/image`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }
};

// FAQ API
export const faqAPI = {
  getAll: async (params?: { category?: string; language?: string; is_active?: boolean; search?: string }) => {
    const response = await api.get('/faq/', { params });
    return response.data;
  },

  getById: async (id: number): Promise<FAQ> => {
    const response = await api.get(`/faq/${id}`);
    return response.data;
  },

  create: async (data: FAQCreate): Promise<FAQ> => {
    const response = await api.post('/faq/', data);
    return response.data;
  },

  update: async (id: number, data: FAQUpdate): Promise<FAQ> => {
    const response = await api.put(`/faq/${id}`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/faq/${id}`);
  },

  reorder: async (orders: { id: number; order: number }[]): Promise<void> => {
    await api.post('/faq/reorder', orders);
  }
};

// Юридичні документи магазину (оплата й доставка, повернення, оферта).
// Список і getBySlug віддають СИРИЙ markdown із {{плейсхолдерами}} — саме його
// редагує адмін; підставлені значення показує лише preview.
// Рецепти: повний CRUD адмінки. Бекенд валідує посилання інгредієнтів на
// товари/фасування і переносить завантажені фото (recipes/_tmp) під slug-ключі.
export const recipesAPI = {
  getAll: async (): Promise<Recipe[]> => {
    const response = await api.get('/admin/recipes');
    return response.data.recipes;
  },

  getById: async (id: number): Promise<Recipe> => {
    const response = await api.get(`/admin/recipes/${id}`);
    return response.data;
  },

  create: async (data: RecipeFields): Promise<Recipe> => {
    const response = await api.post('/admin/recipes', data);
    return response.data;
  },

  update: async (id: number, data: RecipeUpdate): Promise<Recipe> => {
    const response = await api.put(`/admin/recipes/${id}`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/admin/recipes/${id}`);
  },
};

// Модулі магазину (вмикає суперадмін): адмінка ховає розділи вимкнених модулів.
export const modulesAPI = {
  getAll: async (): Promise<Array<{ key: string; name: string; enabled: boolean }>> => {
    const response = await api.get('/admin/modules');
    return response.data;
  },
};

export const legalAPI = {
  getAll: async (): Promise<LegalDocumentSummary[]> => {
    const response = await api.get('/admin/legal');
    return response.data.documents;
  },

  getBySlug: async (slug: string): Promise<LegalDocument> => {
    const response = await api.get(`/admin/legal/${slug}`);
    return response.data;
  },

  create: async (data: LegalDocumentCreate): Promise<LegalDocument> => {
    const response = await api.post('/admin/legal', data);
    return response.data;
  },

  update: async (slug: string, data: LegalDocumentUpdate): Promise<LegalDocument> => {
    const response = await api.put(`/admin/legal/${slug}`, data);
    return response.data;
  },

  delete: async (slug: string): Promise<void> => {
    await api.delete(`/admin/legal/${slug}`);
  },

  // Повертає markdown із підставленими значеннями. body_md опційний: якщо його
  // передати — рендериться незбережений чернетковий текст із редактора.
  preview: async (slug: string, bodyMd?: string): Promise<string> => {
    const response = await api.post(`/admin/legal/${slug}/preview`, { body_md: bodyMd });
    return response.data.body_md;
  },
};

// Delivery Slots API
export const deliverySlotsAPI = {
  getAll: async (params?: { is_active?: boolean }) => {
    const response = await api.get('/delivery-slots/', { params });
    return response.data;
  },

  getById: async (id: number): Promise<DeliverySlot> => {
    const response = await api.get(`/delivery-slots/${id}`);
    return response.data;
  },

  create: async (data: DeliverySlotCreate): Promise<DeliverySlot> => {
    const response = await api.post('/delivery-slots/', data);
    return response.data;
  },

  update: async (id: number, data: Partial<DeliverySlotCreate>): Promise<DeliverySlot> => {
    const response = await api.put(`/delivery-slots/${id}`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/delivery-slots/${id}`);
  },

  checkAvailability: async (slotId: number, deliveryDate: string) => {
    const response = await api.get(`/delivery-slots/${slotId}/availability`, {
      params: { delivery_date: deliveryDate }
    });
    return response.data;
  }
};

// Винятки в графіку доставки: конкретні дати, які магазин закрив або, навпаки,
// відкрив попри звичайний тиждень. Дата — це і є ключ, тому PUT робить upsert.
export const deliveryOverridesAPI = {
  getAll: async (params?: { include_past?: boolean }): Promise<DeliveryDateOverride[]> => {
    const response = await api.get('/delivery-slots/overrides', { params });
    return response.data.items;
  },

  upsert: async (
    date: string,
    data: DeliveryDateOverrideUpsert
  ): Promise<DeliveryDateOverride> => {
    const response = await api.put(`/delivery-slots/overrides/${date}`, data);
    return response.data;
  },

  remove: async (date: string): Promise<void> => {
    await api.delete(`/delivery-slots/overrides/${date}`);
  },
};

// Admin Metrics API
export const adminMetricsAPI = {
  // Get individual admin metrics
  getMetrics: async (adminId?: number, period: 'today' | 'week' | 'month' = 'today') => {
    const params = new URLSearchParams();
    if (adminId) params.append('admin_id', adminId.toString());
    params.append('period', period);

    try {
      const response = await api.get(`/admin/metrics?${params.toString()}`);
      return response.data;
    } catch (error) {
      // Mock data fallback for development
      console.warn('Using mock metrics data. Backend endpoint not available.');
      return {
        admin_id: adminId || 1,
        admin_name: 'Поточний адмін',
        avg_response_time: 180,
        total_conversations: 45,
        resolved_conversations: 38,
        messages_sent: 234,
        messages_received: 189,
        resolution_rate: 84.4,
        customer_satisfaction: 4.2,
        templates_used: 67,
        peak_hour: 14,
        trend: 'up',
        achievements: ['fast_responder', 'high_satisfaction', 'top_resolver']
      };
    }
  },

  // Get team metrics for leaderboard
  getTeamMetrics: async (period: 'today' | 'week' | 'month' = 'today') => {
    const params = new URLSearchParams();
    params.append('period', period);

    try {
      const response = await api.get(`/admin/metrics/team?${params.toString()}`);
      return response.data;
    } catch (error) {
      // Mock data fallback
      console.warn('Using mock team metrics data. Backend endpoint not available.');
      return [
        {
          admin_id: 1,
          admin_name: 'Олександр К.',
          avg_response_time: 120,
          total_conversations: 52,
          resolved_conversations: 48,
          messages_sent: 312,
          messages_received: 287,
          resolution_rate: 92.3,
          customer_satisfaction: 4.6,
          templates_used: 89,
          peak_hour: 15,
          trend: 'up'
        },
        {
          admin_id: 2,
          admin_name: 'Марія П.',
          avg_response_time: 150,
          total_conversations: 48,
          resolved_conversations: 42,
          messages_sent: 276,
          messages_received: 245,
          resolution_rate: 87.5,
          customer_satisfaction: 4.4,
          templates_used: 72,
          peak_hour: 11,
          trend: 'stable'
        },
        {
          admin_id: 3,
          admin_name: 'Дмитро В.',
          avg_response_time: 195,
          total_conversations: 41,
          resolved_conversations: 33,
          messages_sent: 198,
          messages_received: 176,
          resolution_rate: 80.5,
          customer_satisfaction: 4.1,
          templates_used: 54,
          peak_hour: 16,
          trend: 'down'
        }
      ];
    }
  },

  // Get activity timeline
  getActivityTimeline: async (adminId?: number, limit: number = 10) => {
    const params = new URLSearchParams();
    if (adminId) params.append('admin_id', adminId.toString());
    params.append('limit', limit.toString());

    try {
      const response = await api.get(`/admin/metrics/activity?${params.toString()}`);
      return response.data;
    } catch (error) {
      // Mock data fallback
      console.warn('Using mock activity timeline. Backend endpoint not available.');
      return [
        {
          type: 'resolution',
          message: 'Вирішено складне питання клієнта',
          timestamp: new Date(Date.now() - 10 * 60 * 1000).toISOString(),
          color: 'green'
        },
        {
          type: 'milestone',
          message: 'Досягнуто 50 розмов за день',
          timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
          color: 'blue'
        },
        {
          type: 'feedback',
          message: 'Отримано оцінку 5/5 від Gold клієнта',
          timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
          color: 'gold'
        }
      ];
    }
  },

  // Track admin action for analytics
  trackAction: async (action: {
    admin_id: number;
    action_type: string;
    target_id?: number;
    target_type?: string;
    duration?: number;
    metadata?: Record<string, any>;
  }) => {
    try {
      const response = await api.post('/admin/metrics/track', action);
      return response.data;
    } catch (error) {
      console.warn('Failed to track admin action:', error);
      return { success: false };
    }
  },

  // Get achievements list
  getAchievements: async (adminId?: number) => {
    const params = adminId ? `?admin_id=${adminId}` : '';

    try {
      const response = await api.get(`/admin/metrics/achievements${params}`);
      return response.data;
    } catch (error) {
      // Mock data fallback
      console.warn('Using mock achievements data. Backend endpoint not available.');
      return {
        earned: ['fast_responder', 'high_satisfaction', 'top_resolver'],
        available: [
          {
            id: 'fast_responder',
            name: 'Швидка відповідь',
            description: 'Середній час відповіді менше 2 хвилин',
            icon: 'thunder',
            criteria: { avg_response_time: { max: 120 } }
          },
          {
            id: 'high_satisfaction',
            name: 'Висока оцінка',
            description: 'Середня оцінка клієнтів 4.5+',
            icon: 'star',
            criteria: { customer_satisfaction: { min: 4.5 } }
          },
          {
            id: 'top_resolver',
            name: 'Топ вирішувач',
            description: 'Вирішено більше 90% розмов',
            icon: 'trophy',
            criteria: { resolution_rate: { min: 90 } }
          },
          {
            id: 'message_master',
            name: 'Майстер спілкування',
            description: 'Відправлено більше 500 повідомлень',
            icon: 'message',
            criteria: { messages_sent: { min: 500 } }
          },
          {
            id: 'efficiency_expert',
            name: 'Експерт ефективності',
            description: 'Використано більше 100 шаблонів',
            icon: 'rocket',
            criteria: { templates_used: { min: 100 } }
          }
        ]
      };
    }
  }
};

// Company Info API
export const companyInfoAPI = {
  getAll: async (params?: { category?: string; language?: string; is_active?: boolean }) => {
    const response = await api.get('/company-info/', { params });
    return response.data;
  },

  getPublic: async (language: string = 'uk') => {
    const response = await api.get('/company-info/public', { params: { language } });
    return response.data;
  },

  getByKey: async (key: string, language: string = 'uk'): Promise<CompanyInfo> => {
    const response = await api.get(`/company-info/by-key/${key}`, { params: { language } });
    return response.data;
  },

  create: async (data: CompanyInfoCreate): Promise<CompanyInfo> => {
    const response = await api.post('/company-info/', data);
    return response.data;
  },

  update: async (id: number, data: Partial<CompanyInfoCreate>): Promise<CompanyInfo> => {
    const response = await api.put(`/company-info/${id}`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/company-info/${id}`);
  },

  bulkUpdate: async (updates: Record<string, any>, language: string = 'uk'): Promise<void> => {
    await api.post('/company-info/bulk-update', updates, { params: { language } });
  }
};

export const appInfoAPI = {
  get: async (language: string = 'uk'): Promise<AppInfo> => {
    const response = await api.get('/app-info/', { params: { language } });
    return response.data;
  },

  getLanguages: async (): Promise<string[]> => {
    const response = await api.get('/app-info/languages');
    return response.data;
  },

  create: async (data: AppInfoCreate): Promise<AppInfo> => {
    const response = await api.post('/app-info/', data);
    return response.data;
  },

  update: async (data: AppInfoUpdate, language: string = 'uk'): Promise<AppInfo> => {
    const response = await api.put('/app-info/', data, { params: { language } });
    return response.data;
  },

  deactivate: async (language: string): Promise<void> => {
    await api.delete(`/app-info/${language}`);
  }
};

// Branding texts (per-tenant copy catalog) — preview editor on /branding.
export const brandTextsAPI = {
  get: async (): Promise<BrandTextsResponse> => {
    const response = await api.get('/admin/branding-texts/');
    return response.data;
  },

  // `payload.instructions` (optional merchant prompt) rides along on the type.
  generate: async (payload: GenerateBrandingPayload): Promise<{ texts: BrandingTexts }> => {
    const response = await api.post('/admin/branding-texts/generate', payload);
    return response.data;
  },

  // Per-field AI completion: rewrites a single field (or whole string-list) given
  // the full current draft for sibling context + optional merchant instructions.
  completeField: async (payload: {
    field: string;
    current: string | string[];
    texts: BrandingTexts;
    is_list?: boolean;
    instructions?: string;
  }): Promise<{ value: string | string[] }> => {
    const response = await api.post('/admin/branding-texts/complete-field', payload);
    return response.data;
  },

  update: async (texts: BrandingTexts, domain?: string): Promise<BrandTextsResponse> => {
    const response = await api.put('/admin/branding-texts/', { texts, ...(domain ? { domain } : {}) });
    return response.data;
  },
};

// Business Configuration API
export const businessConfigAPI = {
  get: async (): Promise<BusinessConfigResponse> => {
    const response = await api.get("/admin/business-config/");
    return response.data;
  },

  update: async (config: Partial<BusinessConfig> | BusinessConfig): Promise<BusinessConfigResponse> => {
    const response = await api.patch("/admin/business-config/", config);
    return response.data;
  }
};

// Payment / receipt branding (used in PDF receipts). All fields optional strings.
export interface PaymentSettings {
  recipient_name?: string; iban?: string; edrpou?: string; bank_name?: string;
  bank_mfo?: string; bank_code?: string;
  logo_url?: string; website_url?: string; footer_text?: string; contact?: string;
  /** Write-only: send a value to set the shop's acquiring token, omit to keep it. */
  monobank_token?: string;
  /** Read-only: whether this shop has an acquiring token stored. Never the value. */
  monobank_token_set?: boolean;
}
export const paymentSettingsAPI = {
  get: async (): Promise<PaymentSettings> => {
    const response = await api.get("/admin/payment-settings");
    return response.data;
  },
  update: async (settings: PaymentSettings): Promise<PaymentSettings> => {
    const response = await api.put("/admin/payment-settings", settings);
    return response.data;
  },
};

// --- Нова Пошта: ключ і контрагент-відправник магазину ----------------------
// Ключ API write-only, точно як monobank_token: сервер віддає лише
// `api_key_set`. Форма надсилає `api_key` тільки коли оператор ввів новий —
// порожнє поле означало б «лишити збережений», і слати його щоразу зайве.

export type NovaPoshtaCostMode =
  | "recipient_pays"
  | "free_above_threshold"
  | "sender_pays";

export interface NpPackaging {
  id: number;
  name: string;
  width_cm?: number | null;
  length_cm?: number | null;
  height_cm?: number | null;
  tare_weight_kg: number;
  source: 'np' | 'custom';
  np_ref?: string | null;
  is_active: boolean;
  sort_order: number;
}

export interface NpCatalogPackaging {
  ref: string;
  name: string;
  width_cm: number;
  length_cm: number;
  height_cm: number;
}

export interface NovaPoshtaSettings {
  sender_ref?: string | null;
  sender_name?: string | null;
  sender_contact_ref?: string | null;
  sender_contact_name?: string | null;
  sender_phone?: string | null;
  sender_city_ref?: string | null;
  sender_city_name?: string | null;
  sender_warehouse_ref?: string | null;
  sender_warehouse_name?: string | null;
  service_type?: string;
  cargo_type?: string;
  payer_type?: string;
  payment_method?: string;
  description_template?: string;
  default_packaging_id?: number | null;
  postomat_enabled?: boolean;
  cost_mode?: NovaPoshtaCostMode;
  cod_enabled?: boolean;
  cod_payer?: string;
  auto_create_on_status?: string | null;
  tracking_enabled?: boolean;
  /** Write-only: надішли нове значення, щоб замінити ключ; пропусти, щоб лишити. */
  api_key?: string;
  /** Read-only: чи збережений ключ узагалі. Ніколи не саме значення. */
  api_key_set?: boolean;
}

export interface NovaPoshtaContact {
  ref: string;
  name: string;
  phone: string;
}

export interface NovaPoshtaSender {
  ref: string;
  name: string;
  /** Ref міста контрагента в НП, не назва. Порожній — міста в контрагента немає. */
  city_ref: string;
  edrpou: string;
  contacts: NovaPoshtaContact[];
}

export interface NovaPoshtaCity {
  ref: string;
  name: string;
  area: string;
}

export interface NovaPoshtaWarehouse {
  ref: string;
  number: string;
  type: string;
  name: string;
  address: string;
  max_weight: number;
}

/**
 * Довідник НП. Ендпоінти публічні (чекаут ходить туди ж) — адмінка користується
 * тими самими, щоб не тримати другу копію пошуку.
 */
export const novaPoshtaDirectoryAPI = {
  searchCities: async (q: string): Promise<NovaPoshtaCity[]> => {
    const response = await api.get("/nova-poshta/cities", { params: { q, limit: 20 } });
    return response.data;
  },
  searchWarehouses: async (city: string, q = ""): Promise<NovaPoshtaWarehouse[]> => {
    const response = await api.get("/nova-poshta/warehouses", {
      params: { city, q, limit: 30 },
    });
    return response.data;
  },
};

// --- Нова Пошта: накладна замовлення -----------------------------------------

export interface NpShipment {
  id: number;
  ttn_number: string;
  ttn_ref: string;
  status_code?: string | null;
  status_text?: string | null;
  /** Коротко, одним-двома словами; повний текст НП — у status_text. */
  status_label?: string | null;
  cost_on_site?: number | null;
  estimated_delivery?: string | null;
  weight_kg?: number | null;
  declared_cost?: number | null;
  payer_type: string;
  cod_amount?: number | null;
  recipient_last_name?: string | null;
  recipient_first_name?: string | null;
  recipient_middle_name?: string | null;
  recipient_phone?: string | null;
  created_by: string;
  created_at: string;
}

export interface NpShipmentDraft {
  last_name: string;
  first_name: string;
  middle_name: string;
  phone: string;
  weight_kg?: number | null;
  /** Ваговий товар ще не зважено — без ваги оператора створювати не можна. */
  weight_required: boolean;
  seats_amount: number;
  width_cm?: number | null;
  length_cm?: number | null;
  height_cm?: number | null;
  is_postomat: boolean;
  payer_type: string;
  declared_cost: number;
  /** Сума післяплати; null — замовлення передоплачене. */
  cod_amount?: number | null;
  /** Передоплату (IBAN, картка) ще не отримано — посилка поїде без грошей. */
  payment_pending?: boolean;
  /** Обрана упаковка (null для дефолтної) */
  packaging_id?: number | null;
  /** Активні упаковки для вибору */
  packagings?: NpPackaging[];
  /** Вага товару без тари, або null якщо потребує зважування */
  goods_weight_kg?: number | null;
}

export interface NpAlert {
  id: number;
  code: string;
  message: string;
  created_at: string;
}

export interface NpShipmentState {
  configured: boolean;
  shipment: NpShipment | null;
  draft: NpShipmentDraft | null;
  alerts: NpAlert[];
}

export interface NpShipmentCreate {
  last_name?: string;
  first_name?: string;
  middle_name?: string;
  phone?: string;
  weight_kg?: number;
  seats_amount?: number;
  width_cm?: number;
  length_cm?: number;
  height_cm?: number;
  packaging_id?: number;
}

export type NpPrintKind = 'ttn' | 'marking85' | 'marking100';

export const novaPoshtaShipmentAPI = {
  getState: async (orderId: number): Promise<NpShipmentState> => {
    const response = await api.get(`/admin/orders/${orderId}/np/shipment`);
    return response.data;
  },
  /**
   * `idempotencyKey` генерує форма один раз і повторює на кожній спробі: повтор
   * після обірваної відповіді має повернути ту саму накладну, а не створити
   * другу посилку.
   */
  create: async (
    orderId: number,
    body: NpShipmentCreate,
    idempotencyKey: string,
  ): Promise<NpShipment> => {
    const response = await api.post(`/admin/orders/${orderId}/np/shipment`, body, {
      headers: { 'Idempotency-Key': idempotencyKey },
    });
    return response.data;
  },
  remove: async (orderId: number): Promise<{ deleted: boolean }> => {
    const response = await api.delete(`/admin/orders/${orderId}/np/shipment`);
    return response.data;
  },
  /** Файл тягнеться через бекенд: URL друку НП містить ключ API. */
  print: async (orderId: number, kind: NpPrintKind): Promise<Blob> => {
    const response = await api.get(`/admin/orders/${orderId}/np/shipment/print`, {
      params: { kind, format: 'pdf' },
      responseType: 'blob',
    });
    return response.data;
  },
  resolveAlert: async (orderId: number, alertId: number): Promise<{ resolved: boolean }> => {
    const response = await api.post(`/admin/orders/${orderId}/np/alerts/${alertId}/resolve`);
    return response.data;
  },
};

export interface NpScanSheetSummary {
  id: number;
  number: string;
  sheet_date: string | null;
  count: number;
  created_at: string | null;
}

export interface NpDayShipment {
  id: number;
  order_id: number;
  order_number: number;
  ttn_number: string;
  recipient: string;
  recipient_phone: string | null;
  city: string | null;
  branch: string | null;
  status_code: string | null;
  status_text: string | null;
  /** Коротко, одним-двома словами; повний текст НП — у status_text. */
  status_label?: string | null;
  weight_kg: number | null;
  cod_amount: number | null;
  created_at: string | null;
  scan_sheet: { id: number; number: string } | null;
}

export interface NpDay {
  /** null — список без фільтра за днем. */
  date: string | null;
  shipments: NpDayShipment[];
  scan_sheets: NpScanSheetSummary[];
}

export interface NpScanSheetCreated {
  scan_sheet: NpScanSheetSummary;
  added: string[];
  rejected: string[];
}

/** Відправлення за день: реєстри для кур'єра й друк пачкою. */
export const novaPoshtaDispatchAPI = {
  getDay: async (date?: string): Promise<NpDay> => {
    const response = await api.get('/admin/np/shipments', { params: date ? { date } : {} });
    return response.data;
  },
  createScanSheet: async (shipmentIds: number[]): Promise<NpScanSheetCreated> => {
    const response = await api.post('/admin/np/scan-sheets', { shipment_ids: shipmentIds });
    return response.data;
  },
  deleteScanSheet: async (sheetId: number): Promise<{ deleted: boolean }> => {
    const response = await api.delete(`/admin/np/scan-sheets/${sheetId}`);
    return response.data;
  },
  removeFromScanSheet: async (sheetId: number, shipmentId: number): Promise<{ removed: boolean }> => {
    const response = await api.delete(`/admin/np/scan-sheets/${sheetId}/shipments/${shipmentId}`);
    return response.data;
  },
  printScanSheet: async (sheetId: number): Promise<Blob> => {
    const response = await api.get(`/admin/np/scan-sheets/${sheetId}/print`, { responseType: 'blob' });
    return response.data;
  },
  printBatch: async (shipmentIds: number[], kind: NpPrintKind): Promise<Blob> => {
    const response = await api.post(
      '/admin/np/print',
      { shipment_ids: shipmentIds, kind, format: 'pdf' },
      { responseType: 'blob' },
    );
    return response.data;
  },
};

export const novaPoshtaPackagingsAPI = {
  getAll: async (): Promise<NpPackaging[]> => {
    const response = await api.get("/admin/nova-poshta-settings/packagings");
    return response.data;
  },
  create: async (packaging: Omit<NpPackaging, 'id'>): Promise<NpPackaging> => {
    const response = await api.post("/admin/nova-poshta-settings/packagings", packaging);
    return response.data;
  },
  update: async (id: number, packaging: Omit<NpPackaging, 'id'>): Promise<NpPackaging> => {
    const response = await api.put(`/admin/nova-poshta-settings/packagings/${id}`, packaging);
    return response.data;
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/admin/nova-poshta-settings/packagings/${id}`);
  },
  getCatalog: async (): Promise<NpCatalogPackaging[]> => {
    const response = await api.get("/admin/nova-poshta-settings/packagings/np-catalog");
    return response.data;
  },
};

export const novaPoshtaSettingsAPI = {
  get: async (): Promise<NovaPoshtaSettings> => {
    const response = await api.get("/admin/nova-poshta-settings");
    return response.data;
  },
  update: async (settings: NovaPoshtaSettings): Promise<NovaPoshtaSettings> => {
    const response = await api.put("/admin/nova-poshta-settings", settings);
    return response.data;
  },
  /**
   * Перевірити ключ. `apiKey` — щойно введений і ще не збережений; без нього
   * перевіряється збережений. Перевірка нічого не зберігає.
   */
  verify: async (apiKey?: string): Promise<{ senders: NovaPoshtaSender[] }> => {
    const response = await api.post("/admin/nova-poshta-settings/verify", {
      api_key: apiKey || null,
    });
    return response.data;
  },
};

// Onboarding checklist (launch readiness) — derived on the backend.
export interface OnboardingStep {
  key: string;
  done: boolean;
  completed_at: string | null;
  detail: Record<string, any> | null;
  required?: boolean;
}
export interface OnboardingStatus {
  steps: OnboardingStep[];
  progress: { done: number; total: number; required_done?: number; required_total?: number };
  dismissed: boolean;
}
export const onboardingAPI = {
  getStatus: async (): Promise<OnboardingStatus> => {
    const response = await api.get("/admin/onboarding/status");
    return response.data;
  },
  dismiss: async (dismissed: boolean): Promise<{ dismissed: boolean }> => {
    const response = await api.post("/admin/onboarding/dismiss", { dismissed });
    return response.data;
  },
  verifyBot: async (): Promise<{ ok: boolean; username?: string; error?: string }> => {
    const response = await api.post("/admin/onboarding/bot/verify");
    return response.data;
  },
  createAdminChats: async (
    usernames: string[] = [],
  ): Promise<{ chats: AdminChat[]; members: AdminChatMember[] }> => {
    const response = await api.post("/admin/onboarding/admin-chats", { usernames });
    return response.data;
  },
};

export interface AdminChat {
  key: string;
  title: string;
  chat_id?: number;
  invite_link?: string | null;
  created: boolean;
}

export interface AdminChatMember {
  username: string;
  added: boolean;
  promoted: boolean;
  error?: string | null;
}

// Per-platform customizable message templates (curated set).
export interface MessageTemplateItem {
  key: string;
  title: string;
  description: string;
  category: "welcome" | "order" | "status" | "error";
  variables: string[];
  default: string;
  override: string | null;
}
export interface MessageSettingsResponse {
  items: MessageTemplateItem[];
  saved_at: string | null;
}
export const messageSettingsAPI = {
  get: async (): Promise<MessageSettingsResponse> => {
    const response = await api.get("/admin/message-settings");
    return response.data;
  },
  update: async (overrides: Record<string, string>): Promise<MessageSettingsResponse> => {
    const response = await api.put("/admin/message-settings", { overrides });
    return response.data;
  },
};

// Product Statistics API
export const productStatisticsAPI = {
  // Convert query params to URLSearchParams
  buildParams: (params: ProductStatsQuery): URLSearchParams => {
    const searchParams = new URLSearchParams();
    searchParams.append('period', params.period);
    if (params.startDate) searchParams.append('start_date', params.startDate);
    if (params.endDate) searchParams.append('end_date', params.endDate);
    if (params.categoryId) searchParams.append('category_id', params.categoryId);
    if (params.granularity) searchParams.append('granularity', params.granularity);
    if (params.limit) searchParams.append('limit', params.limit.toString());
    if (params.offset) searchParams.append('offset', params.offset.toString());
    if (params.sortBy) searchParams.append('sort_by', params.sortBy);
    if (params.sortOrder) searchParams.append('sort_order', params.sortOrder);
    return searchParams;
  },

  getOverview: async (params: ProductStatsQuery): Promise<ProductStatsOverview> => {
    const searchParams = productStatisticsAPI.buildParams(params);
    const response = await api.get(`/admin/products/statistics/overview?${searchParams.toString()}`);
    // Convert snake_case to camelCase
    const data = response.data;
    return {
      period: data.period,
      startDate: data.start_date,
      endDate: data.end_date,
      totalUnitsSold: data.total_units_sold,
      totalRevenue: data.total_revenue,
      avgOrderValue: data.avg_order_value,
      uniqueProductsSold: data.unique_products_sold,
      totalOrders: data.total_orders,
      unitsChangePercent: data.units_change_percent,
      revenueChangePercent: data.revenue_change_percent,
      ordersChangePercent: data.orders_change_percent,
      topProductId: data.top_product_id,
      topProductName: data.top_product_name,
      topProductRevenue: data.top_product_revenue,
    };
  },

  getSales: async (params: ProductStatsQuery): Promise<ProductSalesResponse> => {
    const searchParams = productStatisticsAPI.buildParams(params);
    const response = await api.get(`/admin/products/statistics/sales?${searchParams.toString()}`);
    const data = response.data;
    return {
      period: data.period,
      items: data.items.map((item: any) => ({
        productId: item.product_id,
        productName: item.product_name,
        categoryId: item.category_id,
        categoryName: item.category_name,
        unitsSold: item.units_sold,
        totalRevenue: item.total_revenue,
        avgPricePerUnit: item.avg_price_per_unit,
        revenueContributionPercent: item.revenue_contribution_percent,
        ordersCount: item.orders_count,
        isFeatured: item.is_featured,
        isActive: item.is_active,
      })),
      totalCount: data.total_count,
      totalRevenue: data.total_revenue,
      page: data.page,
      size: data.size,
    };
  },

  getTrends: async (params: ProductStatsQuery): Promise<ProductTrendResponse> => {
    const searchParams = productStatisticsAPI.buildParams(params);
    const response = await api.get(`/admin/products/statistics/trends?${searchParams.toString()}`);
    const data = response.data;
    return {
      period: data.period,
      granularity: data.granularity,
      dataPoints: data.data_points.map((dp: any) => ({
        date: dp.date,
        label: dp.label,
        unitsSold: dp.units_sold,
        revenue: dp.revenue,
        ordersCount: dp.orders_count,
        avgOrderValue: dp.avg_order_value,
      })),
      overallTrend: data.overall_trend,
      trendPercent: data.trend_percent,
    };
  },

  getCategoryBreakdown: async (params: ProductStatsQuery): Promise<CategoryBreakdownResponse> => {
    const searchParams = productStatisticsAPI.buildParams(params);
    const response = await api.get(`/admin/products/statistics/category-breakdown?${searchParams.toString()}`);
    const data = response.data;
    return {
      period: data.period,
      categories: data.categories.map((cat: any) => ({
        categoryId: cat.category_id,
        categoryName: cat.category_name,
        icon: cat.icon,
        unitsSold: cat.units_sold,
        revenue: cat.revenue,
        revenuePercent: cat.revenue_percent,
        productsCount: cat.products_count,
        avgRevenuePerProduct: cat.avg_revenue_per_product,
      })),
      totalRevenue: data.total_revenue,
    };
  },

  getCategoryProductsBreakdown: async (params: ProductStatsQuery): Promise<CategoryProductsBreakdownResponse> => {
    const searchParams = productStatisticsAPI.buildParams(params);
    const response = await api.get(`/admin/products/statistics/category-products?${searchParams.toString()}`);
    const data = response.data;
    return {
      period: data.period,
      categories: data.categories.map((cat: any) => ({
        categoryId: cat.category_id,
        categoryName: cat.category_name,
        icon: cat.icon,
        totalRevenue: cat.total_revenue,
        totalUnits: cat.total_units,
        totalOrders: cat.total_orders,
        products: cat.products.map((p: any) => ({
          productId: p.product_id,
          productName: p.product_name,
          unitsSold: p.units_sold,
          revenue: p.revenue,
          ordersCount: p.orders_count,
          percentOfCategory: p.percent_of_category,
        })),
      })),
      totalRevenue: data.total_revenue,
    };
  },

  getEngagement: async (params: ProductStatsQuery): Promise<EngagementFunnelResponse> => {
    const searchParams = productStatisticsAPI.buildParams(params);
    const response = await api.get(`/admin/products/statistics/engagement?${searchParams.toString()}`);
    const data = response.data;
    return {
      period: data.period,
      totalViews: data.total_views,
      totalCartAdds: data.total_cart_adds,
      totalPurchases: data.total_purchases,
      overallViewToCart: data.overall_view_to_cart,
      overallCartToPurchase: data.overall_cart_to_purchase,
      overallConversion: data.overall_conversion,
      products: data.products.map((p: any) => ({
        productId: p.product_id,
        productName: p.product_name,
        views: p.views,
        cartAdds: p.cart_adds,
        purchases: p.purchases,
        viewToCartRate: p.view_to_cart_rate,
        cartToPurchaseRate: p.cart_to_purchase_rate,
        overallConversionRate: p.overall_conversion_rate,
      })),
    };
  },

  getTopProducts: async (params: ProductStatsQuery & { metric?: string }): Promise<TopProductsResponse> => {
    const searchParams = productStatisticsAPI.buildParams(params);
    if (params.metric) searchParams.append('metric', params.metric);
    const response = await api.get(`/admin/products/statistics/top-products?${searchParams.toString()}`);
    const data = response.data;
    return {
      period: data.period,
      metric: data.metric,
      topProducts: data.top_products.map((p: any) => ({
        rank: p.rank,
        productId: p.product_id,
        productName: p.product_name,
        categoryName: p.category_name,
        metricValue: p.metric_value,
        metricLabel: p.metric_label,
        changePercent: p.change_percent,
      })),
      worstProducts: data.worst_products.map((p: any) => ({
        rank: p.rank,
        productId: p.product_id,
        productName: p.product_name,
        categoryName: p.category_name,
        metricValue: p.metric_value,
        metricLabel: p.metric_label,
        changePercent: p.change_percent,
      })),
    };
  },

  getComparison: async (params: ProductStatsQuery): Promise<PeriodComparisonResponse> => {
    const searchParams = productStatisticsAPI.buildParams(params);
    const response = await api.get(`/admin/products/statistics/comparison?${searchParams.toString()}`);
    const data = response.data;
    return {
      currentPeriod: data.current_period,
      previousPeriod: data.previous_period,
      currentRevenue: data.current_revenue,
      currentUnits: data.current_units,
      currentOrders: data.current_orders,
      currentAvgOrder: data.current_avg_order,
      previousRevenue: data.previous_revenue,
      previousUnits: data.previous_units,
      previousOrders: data.previous_orders,
      previousAvgOrder: data.previous_avg_order,
      revenueChange: data.revenue_change,
      revenueChangePercent: data.revenue_change_percent,
      unitsChange: data.units_change,
      unitsChangePercent: data.units_change_percent,
      ordersChange: data.orders_change,
      ordersChangePercent: data.orders_change_percent,
      trend: data.trend,
    };
  },

  getCustomerStats: async (params: ProductStatsQuery): Promise<CustomerStats> => {
    const searchParams = productStatisticsAPI.buildParams(params);
    const response = await api.get(`/admin/products/statistics/customer-stats?${searchParams.toString()}`);
    const data = response.data;
    return {
      totalCustomers: data.total_customers,
      newCustomers: data.new_customers,
      repeatCustomers: data.repeat_customers,
      repeatCustomersPercent: data.repeat_customers_percent,
    };
  },

  getOrdersExtended: async (params: ProductStatsQuery): Promise<OrdersExtendedStats> => {
    const searchParams = productStatisticsAPI.buildParams(params);
    const response = await api.get(`/admin/products/statistics/orders-extended?${searchParams.toString()}`);
    const data = response.data;
    return {
      cancelledOrders: data.cancelled_orders,
      cancellationPercent: data.cancellation_percent,
      avgFulfillmentMinutes: data.avg_fulfillment_minutes,
    };
  },

  getDistrictStats: async (params: ProductStatsQuery & { limit?: number }): Promise<DistrictStats[]> => {
    const searchParams = productStatisticsAPI.buildParams(params);
    if (params.limit) searchParams.append('limit', params.limit.toString());
    const response = await api.get(`/admin/products/statistics/district-stats?${searchParams.toString()}`);
    const data = response.data;
    return data.districts.map((d: any) => ({
      districtId: String(d.district_id),
      districtName: d.district_name,
      ordersCount: d.orders_count,
      totalRevenue: d.total_revenue,
    }));
  },

  getWeekdayStats: async (params: ProductStatsQuery): Promise<WeekdayOrderStats[]> => {
    const searchParams = productStatisticsAPI.buildParams(params);
    const response = await api.get(`/admin/products/statistics/weekday-stats?${searchParams.toString()}`);
    const data = response.data;
    return data.weekdays.map((w: any) => ({
      dayOfWeek: w.day_of_week,
      dayLabel: w.day_label,
      ordersCount: w.orders_count,
    }));
  },
};

// User Statistics API
export const userStatisticsAPI = {
  buildParams: (params: UserStatsQuery): URLSearchParams => {
    const searchParams = new URLSearchParams();
    searchParams.append('period', params.period);
    if (params.startDate) searchParams.append('start_date', params.startDate);
    if (params.endDate) searchParams.append('end_date', params.endDate);
    if (params.granularity) searchParams.append('granularity', params.granularity);
    if (params.limit) searchParams.append('limit', params.limit.toString());
    return searchParams;
  },

  getOverview: async (params: UserStatsQuery): Promise<UserStatsOverview> => {
    const searchParams = userStatisticsAPI.buildParams(params);
    const response = await api.get(`/admin/user-statistics/overview?${searchParams.toString()}`);
    const data = response.data;
    return {
      period: data.period,
      startDate: data.start_date,
      endDate: data.end_date,
      totalUsers: data.total_users,
      newUsersInPeriod: data.new_users_in_period,
      activeUsers: data.active_users,
      avgOrdersPerUser: data.avg_orders_per_user,
      avgRevenuePerUser: data.avg_revenue_per_user,
      goldClients: data.gold_clients,
      activeLast7d: data.active_last_7d,
      activeLast28d: data.active_last_28d,
      newUsersChangePercent: data.new_users_change_percent,
      activeUsersChangePercent: data.active_users_change_percent,
      avgOrdersChangePercent: data.avg_orders_change_percent,
      avgRevenueChangePercent: data.avg_revenue_change_percent,
    };
  },

  getSegmentation: async (params: UserStatsQuery): Promise<UserSegmentationResponse> => {
    const searchParams = userStatisticsAPI.buildParams(params);
    const response = await api.get(`/admin/user-statistics/segmentation?${searchParams.toString()}`);
    const data = response.data;
    return {
      period: data.period,
      buckets: data.buckets.map((b: any) => ({
        segmentLabel: b.segment_label,
        userCount: b.user_count,
        percent: b.percent,
        totalRevenue: b.total_revenue,
        avgRevenuePerUser: b.avg_revenue_per_user,
      })),
      totalUsersWithOrders: data.total_users_with_orders,
    };
  },

  getRetention: async (params: UserStatsQuery): Promise<RetentionResponse> => {
    const searchParams = userStatisticsAPI.buildParams(params);
    const response = await api.get(`/admin/user-statistics/retention?${searchParams.toString()}`);
    const data = response.data;
    return {
      period: data.period,
      periodLabels: data.period_labels,
      cohorts: data.cohorts.map((c: any) => ({
        cohortLabel: c.cohort_label,
        cohortSize: c.cohort_size,
        retentionPercents: c.retention_percents,
      })),
    };
  },

  getGrowth: async (params: UserStatsQuery): Promise<UserGrowthResponse> => {
    const searchParams = userStatisticsAPI.buildParams(params);
    const response = await api.get(`/admin/user-statistics/growth?${searchParams.toString()}`);
    const data = response.data;
    return {
      period: data.period,
      granularity: data.granularity,
      dataPoints: data.data_points.map((dp: any) => ({
        date: dp.date,
        label: dp.label,
        newUsers: dp.new_users,
        cumulative: dp.cumulative,
        trend: dp.trend,
      })),
      overallTrend: data.overall_trend,
      trendPercent: data.trend_percent,
    };
  },

  getTopCustomers: async (params: UserStatsQuery & { sortBy?: string }): Promise<TopCustomersResponse> => {
    const searchParams = userStatisticsAPI.buildParams(params);
    if (params.sortBy) searchParams.append('sort_by', params.sortBy);
    const response = await api.get(`/admin/user-statistics/top-customers?${searchParams.toString()}`);
    const data = response.data;
    return {
      period: data.period,
      sortBy: data.sort_by,
      customers: data.customers.map((c: any) => ({
        rank: c.rank,
        userId: c.user_id,
        name: c.name,
        isGold: c.is_gold,
        ordersCount: c.orders_count,
        totalRevenue: c.total_revenue,
        avgOrderValue: c.avg_order_value,
        firstOrderDate: c.first_order_date,
        lastOrderDate: c.last_order_date,
      })),
    };
  },

  getExtendedStats: async (params: UserStatsQuery): Promise<UserExtendedStats> => {
    const searchParams = userStatisticsAPI.buildParams(params);
    const response = await api.get(`/admin/user-statistics/extended-stats?${searchParams.toString()}`);
    const data = response.data;
    return {
      period: data.period,
      blockedUsers: data.blocked_users,
      repeatCustomers: data.repeat_customers,
      repeatCustomersPercent: data.repeat_customers_percent,
      avgDaysBetweenOrders: data.avg_days_between_orders,
      oneTimeCustomers: data.one_time_customers,
      oneTimePercent: data.one_time_percent,
    };
  },

  getRevenueSplit: async (params: UserStatsQuery): Promise<NewVsReturningRevenue> => {
    const searchParams = userStatisticsAPI.buildParams(params);
    const response = await api.get(`/admin/user-statistics/revenue-split?${searchParams.toString()}`);
    const data = response.data;
    return {
      period: data.period,
      newCustomersCount: data.new_customers_count,
      newCustomersRevenue: data.new_customers_revenue,
      newCustomersPercent: data.new_customers_percent,
      returningCustomersCount: data.returning_customers_count,
      returningCustomersRevenue: data.returning_customers_revenue,
      returningCustomersPercent: data.returning_customers_percent,
      totalRevenue: data.total_revenue,
    };
  },

  getActivityHeatmap: async (params: UserStatsQuery): Promise<ActivityHeatmapResponse> => {
    const searchParams = userStatisticsAPI.buildParams(params);
    const response = await api.get(`/admin/user-statistics/activity-heatmap?${searchParams.toString()}`);
    const data = response.data;
    return {
      period: data.period,
      cells: data.cells.map((c: any) => ({
        dayOfWeek: c.day_of_week,
        dayLabel: c.day_label,
        timeSlot: c.time_slot,
        timeLabel: c.time_label,
        ordersCount: c.orders_count,
      })),
      maxOrders: data.max_orders,
    };
  },

  getPaymentMethods: async (params: UserStatsQuery): Promise<PaymentMethodsResponse> => {
    const searchParams = userStatisticsAPI.buildParams(params);
    const response = await api.get(`/admin/user-statistics/payment-methods?${searchParams.toString()}`);
    const data = response.data;
    return {
      period: data.period,
      methods: data.methods.map((m: any) => ({
        method: m.method,
        label: m.label,
        ordersCount: m.orders_count,
        percent: m.percent,
        totalRevenue: m.total_revenue,
      })),
      totalOrders: data.total_orders,
    };
  },

  getActiveUsersTrend: async (params: UserStatsQuery): Promise<ActiveUsersTrendResponse> => {
    const searchParams = userStatisticsAPI.buildParams(params);
    const response = await api.get(`/admin/user-statistics/active-users-trend?${searchParams.toString()}`);
    const data = response.data;
    return {
      period: data.period,
      granularity: data.granularity,
      dataPoints: data.data_points.map((dp: any) => ({
        date: dp.date,
        label: dp.label,
        active7d: dp.active_7d,
        active28d: dp.active_28d,
      })),
    };
  },

  getBroadcastStats: async (params: UserStatsQuery): Promise<BroadcastStatsResponse> => {
    const searchParams = userStatisticsAPI.buildParams(params);
    const response = await api.get(`/admin/user-statistics/broadcast-stats?${searchParams.toString()}`);
    const data = response.data;
    return {
      period: data.period,
      totalCampaigns: data.total_campaigns,
      totalMessagesSent: data.total_messages_sent,
      totalUsersReached: data.total_users_reached,
      avgDeliveryRate: data.avg_delivery_rate,
      totalFailed: data.total_failed,
      campaigns: data.campaigns.map((c: any) => ({
        campaignId: c.campaign_id,
        messagePreview: c.message_preview,
        createdAt: c.created_at,
        targetType: c.target_type,
        totalUsers: c.total_users,
        sentCount: c.sent_count,
        failedCount: c.failed_count,
        deliveryRate: c.delivery_rate,
      })),
    };
  },

  getVisitorsTrend: async (params: UserStatsQuery): Promise<VisitorsTrendResponse> => {
    const searchParams = userStatisticsAPI.buildParams(params);
    const response = await api.get(`/admin/user-statistics/visitors-trend?${searchParams.toString()}`);
    const data = response.data;
    return {
      period: data.period,
      granularity: data.granularity,
      dataPoints: data.data_points.map((dp: any) => ({
        date: dp.date,
        label: dp.label,
        visitors7d: dp.visitors_7d,
        visitors28d: dp.visitors_28d,
        botVisitors7d: dp.bot_visitors_7d || 0,
        botVisitors28d: dp.bot_visitors_28d || 0,
      })),
    };
  },

  getWebFunnel: async (params: UserStatsQuery): Promise<WebFunnelResponse> => {
    const searchParams = new URLSearchParams();
    searchParams.append('period', params.period);
    if (params.startDate) searchParams.append('start_date', params.startDate);
    if (params.endDate) searchParams.append('end_date', params.endDate);
    const response = await api.get(`/admin/user-statistics/web-funnel?${searchParams.toString()}`);
    const data = response.data;
    return {
      period: data.period,
      startDate: data.start_date,
      endDate: data.end_date,
      visitors: data.visitors,
      identified: data.identified,
      loggedIn: data.logged_in,
      ordered: data.ordered,
      identifiedRate: data.identified_rate,
      orderRate: data.order_rate,
      overallRate: data.overall_rate,
      trend: data.trend.map((p: any): WebFunnelTrendPoint => ({
        date: p.date,
        label: p.label,
        visitors: p.visitors,
      })),
    };
  },

  getEngagementStats: async (params: UserStatsQuery): Promise<EngagementStatsResponse> => {
    const searchParams = userStatisticsAPI.buildParams(params);
    const response = await api.get(`/admin/user-statistics/engagement-stats?${searchParams.toString()}`);
    const data = response.data;
    return {
      period: data.period,
      activitySegments: data.activity_segments.map((s: any) => ({
        segment: s.segment,
        label: s.label,
        count: s.count,
        percent: s.percent,
      })),
      totalUsersWithInteraction: data.total_users_with_interaction,
      avgTimeToFirstInteractionHours: data.avg_time_to_first_interaction_hours,
      medianTimeToFirstInteractionHours: data.median_time_to_first_interaction_hours,
      interactionSegments: data.interaction_segments.map((s: any) => ({
        segment: s.segment,
        label: s.label,
        count: s.count,
        percent: s.percent,
      })),
      avgInteractionsPerDay: data.avg_interactions_per_day,
      avgInteractionsPerUser: data.avg_interactions_per_user,
    };
  },
};

// Campaigns API
export const campaignsAPI = {
  getAll: async (
    page = 1,
    size = 20,
    source?: string,
    isActive?: boolean,
    search?: string
  ): Promise<{ campaigns: Campaign[]; total: number; page: number; size: number }> => {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
    });
    if (source) params.append('source', source);
    if (isActive !== undefined) params.append('is_active', isActive.toString());
    if (search) params.append('search', search);

    const response = await api.get(`/admin/campaigns/?${params.toString()}`);
    return response.data;
  },

  get: async (id: number): Promise<Campaign> => {
    const response = await api.get(`/admin/campaigns/${id}`);
    return response.data;
  },

  create: async (data: CampaignCreate): Promise<Campaign> => {
    const response = await api.post('/admin/campaigns/', data);
    return response.data;
  },

  update: async (id: number, data: CampaignUpdate): Promise<Campaign> => {
    const response = await api.put(`/admin/campaigns/${id}`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/admin/campaigns/${id}`);
  },

  getOverview: async (): Promise<CampaignOverview> => {
    const response = await api.get('/admin/campaigns/overview');
    return response.data;
  },

  getCampaignStats: async (id: number): Promise<CampaignStats> => {
    const response = await api.get(`/admin/campaigns/${id}/stats`);
    return response.data;
  },
};

// Stock / Warehouse API
export const stockAPI = {
  getWarehouse: async (params?: { category_id?: string; stock_status?: string; search?: string; page?: number; size?: number }): Promise<WarehouseOverview> => {
    const response = await api.get('/admin/stock/', { params });
    return response.data;
  },

  getPackageStock: async (packageId: number): Promise<StockInfo> => {
    const response = await api.get(`/admin/stock/${packageId}`);
    return response.data;
  },

  restock: async (packageId: number, data: StockRestock): Promise<StockMovement> => {
    const response = await api.post(`/admin/stock/${packageId}/restock`, data);
    return response.data;
  },

  adjustStock: async (packageId: number, data: StockAdjustment): Promise<StockMovement> => {
    const response = await api.put(`/admin/stock/${packageId}/adjust`, data);
    return response.data;
  },

  writeOff: async (packageId: number, data: StockWriteOff): Promise<StockMovement> => {
    const response = await api.post(`/admin/stock/${packageId}/write-off`, data);
    return response.data;
  },

  getMovements: async (params?: { package_id?: number; order_id?: number; type?: string; limit?: number; offset?: number }): Promise<StockMovement[]> => {
    const response = await api.get('/admin/stock/movements', { params });
    return response.data;
  },

  getPackageMovements: async (packageId: number, params?: { limit?: number; offset?: number }): Promise<PaginatedStockMovements> => {
    const response = await api.get(`/admin/stock/${packageId}/movements`, { params });
    return response.data;
  },

  getAlerts: async (): Promise<StockAlert[]> => {
    const response = await api.get('/admin/stock/alerts');
    return response.data;
  },
};

// Deliveries API
export const deliveriesAPI = {
  getAll: async (status?: string): Promise<DeliveryResponse[]> => {
    const params = status ? { status } : {};
    const response = await api.get('/admin/deliveries/', { params });
    return response.data;
  },

  getById: async (id: number): Promise<DeliveryResponse> => {
    const response = await api.get(`/admin/deliveries/${id}`);
    return response.data;
  },

  create: async (data: DeliveryCreate): Promise<DeliveryResponse> => {
    const response = await api.post('/admin/deliveries/', data);
    return response.data;
  },

  update: async (id: number, data: Partial<DeliveryResponse>): Promise<DeliveryResponse> => {
    const response = await api.put(`/admin/deliveries/${id}`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/admin/deliveries/${id}`);
  },

  receive: async (id: number, data: ReceiveDeliveryInput): Promise<DeliveryResponse> => {
    const response = await api.post(`/admin/deliveries/${id}/receive`, data);
    return response.data;
  },

  createInitialInventory: async (): Promise<DeliveryResponse> => {
    const response = await api.post('/admin/deliveries/initial-inventory');
    return response.data;
  },
};

// Shelves API
export const shelvesAPI = {
  getAll: async (): Promise<ShelfResponse[]> => {
    const response = await api.get('/admin/shelves/');
    return response.data;
  },

  create: async (data: { name: string; location?: string; sort_order?: number }): Promise<ShelfResponse> => {
    const response = await api.post('/admin/shelves/', data);
    return response.data;
  },

  update: async (id: number, data: Partial<ShelfResponse>): Promise<ShelfResponse> => {
    const response = await api.put(`/admin/shelves/${id}`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/admin/shelves/${id}`);
  },

  getPackages: async (shelfId: number): Promise<ShelfAssignmentResponse[]> => {
    const response = await api.get(`/admin/shelves/${shelfId}/packages`);
    return response.data;
  },

  assignPackage: async (shelfId: number, packageId: number): Promise<ShelfAssignmentResponse> => {
    const response = await api.post(`/admin/shelves/${shelfId}/packages`, { package_id: packageId });
    return response.data;
  },

  removePackage: async (shelfId: number, assignmentId: number): Promise<void> => {
    await api.delete(`/admin/shelves/${shelfId}/packages/${assignmentId}`);
  },
};

// --- Abandoned Cart Notifications ---

export interface AbandonedCartSettings {
  enabled: boolean;
  delay_minutes: number;
  cooldown_hours: number;
  max_notifications: number;
  ttl_days: number;
  cron_interval_minutes: number;
  min_cart_total: number;
  message_template: string;
  button_text: string;
  ai_reminders_enabled: boolean;
  ai_ab_split_percent: number;
  updated_at?: string;
}

export interface PeriodStats {
  synced: number;
  notified: number;
  returned: number;
  ordered: number;
  conversion_rate: number;
}

export interface AbandonedCartStats {
  today: PeriodStats;
  week: PeriodStats;
  month: PeriodStats;
}

export type AbandonedCartStatus = 'pending' | 'notified' | 'returned' | 'ordered';

export interface AbandonedCartUserInfo {
  id: number;
  first_name: string;
  last_name?: string | null;
  username?: string | null;
  phone?: string | null;
  is_gold_client: boolean;
  is_blocked: boolean;
  orders_count: number;
  created_at?: string | null;
  last_bot_interaction?: string | null;
}

export interface AbandonedCartLineItem {
  product_id: string;
  product_name: string;
  package_id: string;
  package_name: string;
  weight: number;
  unit: string;
  quantity: number;
  price_per_unit: number;
  total_price: number;
  image_url?: string | null;
}

export interface AbandonedCartListItem {
  user: AbandonedCartUserInfo;
  total: number;
  items_count: number;
  top_product?: string | null;
  notified_count: number;
  last_notified_at?: string | null;
  updated_at?: string | null;
  status: AbandonedCartStatus;
}

export interface AbandonedCartListResponse {
  items: AbandonedCartListItem[];
  total: number;
  page: number;
  page_size: number;
}

export interface AbandonedCartEventItem {
  event_type: string;
  cart_total?: number | null;
  items_count?: number | null;
  metadata?: Record<string, any> | null;
  created_at?: string | null;
}

export interface AbandonedCartDetail {
  user: AbandonedCartUserInfo;
  items: AbandonedCartLineItem[];
  total: number;
  items_count: number;
  notified_count: number;
  last_notified_at?: string | null;
  updated_at?: string | null;
  created_at?: string | null;
  status: AbandonedCartStatus;
  events: AbandonedCartEventItem[];
}

export interface AbandonedCartListParams {
  page?: number;
  page_size?: number;
  search?: string;
  sort_by?: 'updated_at' | 'total';
  order?: 'asc' | 'desc';
  min_total?: number;
}

export const abandonedCartsAPI = {
  getSettings: async (): Promise<AbandonedCartSettings> => {
    const response = await api.get('/admin/abandoned-carts/settings');
    return response.data;
  },

  updateSettings: async (data: Partial<AbandonedCartSettings>): Promise<AbandonedCartSettings> => {
    const response = await api.patch('/admin/abandoned-carts/settings', data);
    return response.data;
  },

  getStats: async (): Promise<AbandonedCartStats> => {
    const response = await api.get('/admin/abandoned-carts/stats');
    return response.data;
  },

  testSend: async (userId: number): Promise<void> => {
    await api.post('/admin/abandoned-carts/test-send', { user_id: userId });
  },

  listCarts: async (params: AbandonedCartListParams = {}): Promise<AbandonedCartListResponse> => {
    const response = await api.get('/admin/abandoned-carts/carts', { params });
    return response.data;
  },

  getCartDetail: async (userId: number): Promise<AbandonedCartDetail> => {
    const response = await api.get(`/admin/abandoned-carts/carts/${userId}`);
    return response.data;
  },
};

// Pickup Location API — singleton store-managed pickup address
export const pickupLocationAPI = {
  get: async () => {
    const response = await api.get('/pickup-location');
    return response.data as import('../types').PickupLocation;
  },

  update: async (
    payload: Partial<import('../types').PickupLocation> & { address: string }
  ) => {
    const response = await api.put('/admin/pickup-location', payload);
    return response.data as import('../types').PickupLocation;
  },
};

// Current tenant context (which org/platform this admin session is operating in),
// resolved from the backend rather than the hardcoded CLIENT_BRAND constant.
export interface AdminContext {
  platform: {
    slug: string;
    brand_name: string;
    theme?: Record<string, unknown> | null;
    // Public storefront origin for this tenant (custom_domain or
    // {slug}.{STOREFRONT_DOMAIN_SUFFIX}). Drives the /branding live preview.
    storefront_url: string;
  };
  organization: { slug: string; name: string } | null;
}

export const contextAPI = {
  get: async (): Promise<AdminContext> => {
    const response = await api.get('/admin/context');
    return response.data;
  },
};

export interface AdminReview {
  id: number;
  product_id: string;
  product_name?: string | null;
  user_id: number;
  user_name: string;
  rating: number;
  text?: string | null;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
}

export interface OrderReview {
  order_id: number;
  rating: number;
  comment?: string | null;
  created_at: string;
  user_id?: number | null;
  user_name: string;
  answers?: Record<string, string | string[]> | null;
}

export const reviewsAPI = {
  list: (status: string = 'pending') =>
    api.get<{ reviews: AdminReview[]; total: number }>('/admin/reviews', { params: { status } })
       .then(r => r.data),
  moderate: (id: number, status: 'approved' | 'rejected') =>
    api.patch<AdminReview>(`/admin/reviews/${id}`, { status }).then(r => r.data),
  listOrderReviews: (page = 1, size = 20, rating?: number) =>
    api.get<{ items: OrderReview[]; total: number }>('/admin/reviews/orders', {
      params: { page, size, ...(rating ? { rating } : {}) },
    }).then(r => r.data),
};

// --- Запити товарів, яких немає в каталозі (модуль product_requests) ---------

export type ProductRequestStatus = 'new' | 'searching' | 'added' | 'declined';

export interface ProductRequestCustomer {
  user_id?: number | null;
  name?: string | null;
  username?: string | null;
  email?: string | null;
  kind: 'telegram' | 'web' | 'guest';
}

export interface ProductRequestItem {
  id: number;
  query: string;
  details?: string | null;
  source?: string | null;
  created_at: string;
  customer: ProductRequestCustomer;
}

export interface ProductRequestGroup {
  id: number;
  title: string;
  query_key: string;
  status: ProductRequestStatus;
  product: { id: string; name: string; image_url?: string | null } | null;
  people_count: number;
  last_requested_at: string;
  created_at: string;
  closed_at?: string | null;
  notified_at?: string | null;
  requests: ProductRequestItem[];
}

export interface SearchMiss {
  query_key: string;
  sample_query: string;
  count: number;
  days_seen: number;
  last_seen_at: string;
  open_group_id?: number | null;
}

export const productRequestsAPI = {
  list: (status: 'open' | 'closed', page = 1, size = 50) =>
    api.get<{ items: ProductRequestGroup[]; total: number; page: number; size: number }>(
      '/admin/product-requests', { params: { status, page, size } },
    ).then(r => r.data),
  update: (groupId: number, payload: { status: 'searching' | 'added' | 'declined'; product_id?: string }) =>
    api.patch<ProductRequestGroup>(`/admin/product-requests/${groupId}`, payload).then(r => r.data),
  merge: (groupId: number, intoGroupId: number) =>
    api.post<ProductRequestGroup>(`/admin/product-requests/${groupId}/merge`, { into_group_id: intoGroupId })
       .then(r => r.data),
  searchMisses: (days = 30, limit = 50) =>
    api.get<{ items: SearchMiss[] }>('/admin/product-requests/search-misses', { params: { days, limit } })
       .then(r => r.data),
};

export const manualOrdersAPI = {
  preview: (payload: ManualOrderPayload) =>
    api.post<ManualOrderPreview>('/admin/orders/preview', payload).then(r => r.data),
  create: (payload: ManualOrderPayload) =>
    api.post<ManualOrderCreated>('/admin/orders', payload).then(r => r.data),
};

export const orderEditAPI = {
  // `id` тут — первинний ключ рядка замовлення (Order.id), той самий, яким
  // адмінка вже відкриває картку, а не людський order_id.
  preview: (id: number, payload: OrderEditPayload) =>
    api.post<OrderEditPreview>(`/admin/orders/${id}/preview`, payload).then(r => r.data),
  update: (id: number, payload: OrderEditPayload) =>
    api.put<OrderEditResult>(`/admin/orders/${id}`, payload).then(r => r.data),
  // Закриває недоплату (готівка/IBAN, отримана на видачі) без правки решти
  // замовлення. Ендпоінт з'являється разом із грошовим планом — до того часу
  // виклик відповість 404.
  settleOutstanding: (id: number) =>
    api.post<OrderEditResult>(`/admin/orders/${id}/settle-outstanding`).then(r => r.data),
  // Дзеркало settleOutstanding для від'ємного балансу: фіксує, що магазин
  // повернув гроші клієнту (готівкою/IBAN), без правки решти замовлення.
  settleRefund: (id: number) =>
    api.post<OrderEditResult>(`/admin/orders/${id}/settle-refund`).then(r => r.data),
};

export const paymentMethodsAPI = {
  get: () =>
    api
      .get<{
        card_online: boolean;
        nova_poshta_cod?: boolean;
        nova_poshta_cod_fee_payer?: 'Recipient' | 'Sender' | null;
      }>('/payment-methods')
      .then(r => r.data),
};

// Export the raw api instance for advanced usage (like in Communication page)
export { api };

export default api;
